import { Link } from 'react-router-dom';
import { ShoppingCart, Sparkles, MapPin, Truck, BadgeCheck, Boxes, Heart, Star, Scale, BookmarkPlus, Megaphone } from 'lucide-react';
import { formatCOP } from '@/lib/format';
import { useFavorites } from '@/lib/favorites';
import { useWishlist } from '@/lib/wishlist';
import { useCompare } from '@/lib/compare';
import { useAuth } from '@/lib/auth';
import { resolveImageUrl } from '@/components/ImageUploader';
import { toast } from 'sonner';
import { CATALOG } from '@/constants/testIds';

const PLACEHOLDER = 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=600';

export const ProductCard = ({ product, onAdd }) => {
  const img = resolveImageUrl((product.images || [])[0]) || PLACEHOLDER;
  const isPro = product.seller_plan === 'pro';
  // Un producto está en promoción vigente si `sponsored_until` es una fecha
  // futura. Al vencer, ese comparativo pasa a false y el badge desaparece.
  const isSponsored = !!(product.sponsored_until && new Date(product.sponsored_until) > new Date());
  const isOriginal = product.condition === 'original';
  const compatPreview = (product.vehicle_compat || []).slice(0, 2).join(' · ');
  const deliveryDays = product.delivery_days || 3;
  const fav = useFavorites();
  const wish = useWishlist();
  const cmp = useCompare();
  const { user } = useAuth();
  const isFav = fav?.isFav?.(product.product_id);
  const isWish = wish?.isOnWishlist?.(product.product_id);
  const isCmp = cmp?.has?.(product.product_id);

  const toggleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error('Inicia sesión para guardar favoritos'); return; }
    if (user.role !== 'buyer') { toast.error('Solo compradores pueden guardar favoritos'); return; }
    const added = await fav.toggle(product.product_id);
    toast.success(added ? 'Añadido a favoritos' : 'Eliminado de favoritos');
  };

  const toggleWish = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error('Inicia sesión para usar la lista de deseos'); return; }
    if (user.role !== 'buyer') { toast.error('Solo compradores pueden usar la lista de deseos'); return; }
    const added = await wish.toggle(product.product_id);
    toast.success(added ? 'Añadido a tu lista de deseos' : 'Quitado de tu lista de deseos');
  };

  const toggleCmp = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isCmp && cmp.count >= cmp.max) { toast.error(`Máximo ${cmp.max} productos para comparar`); return; }
    cmp.toggle(product.product_id);
    toast.success(isCmp ? 'Quitado del comparador' : 'Añadido al comparador');
  };

  return (
    <div
      data-testid={CATALOG.productCard(product.product_id)}
      className={`group border bg-white flex flex-col relative rounded-md shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${
        isSponsored ? 'border-orange-500 ring-2 ring-orange-200' : isPro ? 'border-orange-400' : 'border-slate-200 hover:border-slate-400'
      }`}
    >
      {isSponsored && (
        <div
          data-testid={`product-sponsored-badge-${product.product_id}`}
          className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 bg-orange-500 text-white text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 shadow"
        >
          <Megaphone className="h-2.5 w-2.5" /> Promocionado
        </div>
      )}
      {isPro && !isSponsored && (
        <div className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 bg-orange-500 text-slate-900 text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1">
          <Sparkles className="h-2.5 w-2.5" /> PRO
        </div>
      )}
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5">
        <button
          onClick={toggleCmp}
          data-testid={`cmp-btn-${product.product_id}`}
          className={`h-7 w-7 flex items-center justify-center bg-white border ${isCmp ? 'border-orange-500 bg-orange-50' : 'border-slate-300'} hover:border-orange-500 transition-colors`}
          aria-label="Comparar"
          title="Agregar al comparador"
        >
          <Scale className={`h-3.5 w-3.5 ${isCmp ? 'text-orange-600' : 'text-slate-400'}`} />
        </button>
        <button
          onClick={toggleFav}
          data-testid={`fav-btn-${product.product_id}`}
          className={`h-7 w-7 flex items-center justify-center bg-white border ${isFav ? 'border-rose-500' : 'border-slate-300'} hover:border-rose-500 transition-colors`}
          aria-label="Favorito"
        >
          <Heart className={`h-3.5 w-3.5 ${isFav ? 'fill-rose-500 text-rose-500' : 'text-slate-400'}`} />
        </button>
        <button
          onClick={toggleWish}
          data-testid={`wish-btn-${product.product_id}`}
          className={`h-7 w-7 flex items-center justify-center bg-white border ${isWish ? 'border-amber-500 bg-amber-50' : 'border-slate-300'} hover:border-amber-500 transition-colors`}
          aria-label="Lista de deseos"
          title="Lista de deseos"
        >
          <BookmarkPlus className={`h-3.5 w-3.5 ${isWish ? 'text-amber-600' : 'text-slate-400'}`} />
        </button>
        <span className={`inline-block text-[9px] font-bold uppercase tracking-wider px-2 py-1 ${
          isOriginal ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-700'
        }`}>
          {isOriginal ? 'Original' : 'Genérico'}
        </span>
      </div>

      <Link
        data-testid={CATALOG.productLink(product.product_id)}
        to={`/producto/${product.slug || product.product_id}`}
        className="block aspect-square overflow-hidden bg-slate-50 rounded-t-md"
      >
        <img
          src={img}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
          onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
        />
      </Link>

      <div className="p-3 flex-1 flex flex-col">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
          {product.brand} · {product.category}
        </div>
        <Link to={`/producto/${product.slug || product.product_id}`} className="mt-1 block">
          <h3 className="font-display font-semibold text-sm text-slate-900 leading-snug line-clamp-2 hover:text-blue-700 min-h-[2.5rem]">
            {product.name}
          </h3>
        </Link>
        <div className="mt-1 text-[11px] text-slate-500 font-mono">Ref: {product.reference}</div>

        {compatPreview && (
          <div className="mt-1 text-[11px] text-slate-600 line-clamp-1" title={(product.vehicle_compat || []).join(' · ')}>
            <Boxes className="inline h-3 w-3 mr-1 -mt-0.5 text-slate-400" />
            {compatPreview}{(product.vehicle_compat || []).length > 2 && ` +${product.vehicle_compat.length - 2}`}
          </div>
        )}

        <div className="mt-2 space-y-0.5 text-[11px] text-slate-600 border-t border-slate-100 pt-2">
          {product.seller_name && (
            <div className="truncate flex items-center gap-1">
              <BadgeCheck className="h-3 w-3 text-emerald-600 shrink-0" />
              <Link
                to={`/tienda/${product.seller_id}`}
                onClick={(e) => e.stopPropagation()}
                data-testid={`seller-link-${product.product_id}`}
                className="truncate flex-1 hover:text-blue-700 hover:underline"
              >
                {product.seller_name}
              </Link>
              {product.seller_rating != null && (
                <span className="inline-flex items-center gap-0.5 text-slate-700">
                  <Star className="h-3 w-3 fill-orange-500 text-orange-500" />
                  <span className="font-semibold">{product.seller_rating}</span>
                  <span className="text-slate-400">({product.seller_reviews_count})</span>
                </span>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {product.seller_city && (
              <span className="inline-flex items-center gap-1 min-w-0">
                <MapPin className="h-3 w-3 text-slate-400 shrink-0" /><span className="truncate">{product.seller_city}</span>
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <Truck className="h-3 w-3 text-slate-400" />{deliveryDays}d
            </span>
          </div>
        </div>

        <div className="mt-2 pt-2 flex items-end justify-between gap-2">
          <div>
            {product.has_promotion ? (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] line-through text-slate-400" data-testid={`product-price-original-${product.product_id}`}>
                    {formatCOP(product.original_price)}
                  </span>
                  <span
                    data-testid={`product-discount-badge-${product.product_id}`}
                    className="inline-flex items-center gap-0.5 bg-orange-100 text-orange-700 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                  >
                    −{product.discount_pct}%
                  </span>
                </div>
                <div
                  data-testid={`product-price-effective-${product.product_id}`}
                  className="font-display text-lg font-black text-orange-600 leading-tight"
                >
                  {formatCOP(product.effective_price)}
                </div>
              </>
            ) : (
              <div className="font-display text-lg font-black text-slate-900 leading-tight" data-testid={`product-price-${product.product_id}`}>
                {formatCOP(product.price)}
              </div>
            )}
            <div className={`text-[10px] ${product.stock > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
              {product.stock > 0 ? `${product.stock} en stock` : 'Agotado'}
            </div>
          </div>
          {onAdd && (
            <button
              onClick={() => onAdd(product)}
              disabled={product.stock === 0}
              className="inline-flex items-center gap-1 bg-slate-900 text-white px-2.5 py-1.5 text-[11px] font-semibold hover:bg-blue-700 disabled:opacity-40"
            >
              <ShoppingCart className="h-3 w-3" />
              Agregar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
